import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Link } from "react-router";

import { useOrganization } from "@/auth/organization";
import {
  Badge,
  Button,
  buttonClass,
  cx,
  EmptyState,
  ErrorState,
  FullPageSpinner,
  Section,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRow,
  type Requirement,
  useToast,
} from "@/components/ui";
import {
  domainKeys,
  domainsApi,
  has,
  type ConnectedRow,
  type DnsChange,
  type DnsPreview,
  type DnsRecord,
  type DomainDetail,
  type PillTone,
  type Zone,
} from "@/lib/domains";

import { AttentionSection } from "../dns/Attention";
import { RecordModal } from "../dns/RecordModal";
import { RemoveModal } from "../dns/RemoveModal";
import { ReviewModal } from "../dns/ReviewModal";
import { TemplatesSection } from "../dns/TemplatesSection";
import { ZoneFileSection } from "../dns/ZoneFileSection";
import { changeFor, draftFor, isDraftEdited, recordValue, type RecordDraft } from "../dns/util";
import { useUnsavedChanges } from "@/lib/unsavedChanges";
import { DomainIcon, Notice, ConnectRow } from "@/components/domains/parts";

const CONNECTED_PILL: Record<ConnectedRow["status"], { label: string; tone: PillTone }> = {
  connected: { label: "Connected", tone: "success" },
  pending: { label: "Pending", tone: "warning" },
  needs_fix: { label: "Needs fix", tone: "danger" },
  active: { label: "Active", tone: "neutral" },
  none: { label: "Not set up", tone: "neutral" },
};

/** Figma 2039-180 "DNS" (4724-6466 when something needs attention), with its record modals. */
export function DnsTab({ detail }: { detail: DomainDetail }) {
  const d = detail.domain;
  const { orgId, canOn } = useOrganization();
  const qc = useQueryClient();
  const zoneKey = domainKeys.zone(orgId, d.id);
  const zone = useQuery({
    queryKey: zoneKey,
    queryFn: () => domainsApi.zone(orgId, d.id),
    refetchInterval: (q) => (q.state.data?.records.some((r) => r.status === "pending") ? 4000 : false),
  });

  const [draft, setDraft] = useState<RecordDraft | null>(null);
  const [stage, setStage] = useState<"edit" | "review" | null>(null);
  const [review, setReview] = useState<{ change: DnsChange; preview: DnsPreview } | null>(null);
  const [removal, setRemoval] = useState<{ records: DnsRecord[]; kept: DnsRecord | null } | null>(null);

  const applied = async (next: Zone) => {
    qc.setQueryData(zoneKey, next);
    await qc.invalidateQueries({ queryKey: domainKeys.all(orgId) });
  };
  const preview = useMutation({
    mutationFn: (change: DnsChange) => domainsApi.previewDns(orgId, d.id, [change]),
    onSuccess: (result, change) => {
      setReview({ change, preview: result });
      setStage("review");
    },
  });
  const toast = useToast();
  const apply = useMutation({
    mutationFn: (changes: DnsChange[]) =>
      domainsApi.applyDns(orgId, d.id, changes, zone.data?.zone_version ?? 0, crypto.randomUUID()),
    onSuccess: async (next) => {
      await applied(next);
      toast.show({ tone: "success", title: "DNS changes applied" });
      setStage(null);
      setDraft(null);
      setReview(null);
      setRemoval(null);
    },
  });
  const recheck = useMutation({
    mutationFn: () => domainsApi.recheckDns(orgId, d.id),
    onSuccess: applied,
  });

  // The record dialog guards the edit step; at the review step the draft is still unapplied, and closing the
  // review (or leaving the page) would throw it away.
  const reviewing = useUnsavedChanges(
    stage === "review" && draft !== null && isDraftEdited(draft) && !apply.isPending,
    { ignoreSearch: true },
  );

  if (zone.isPending) return <FullPageSpinner />;
  if (zone.isError) return <ErrorState error={zone.error} onRetry={() => void zone.refetch()} />;
  const z = zone.data;
  // Records can be edited here only while CortexGrip hosts the zone; then the actions show to everyone
  // who can open the domain, unavailable (with the reason) without dns:manage on its folder.
  const editable = z.managed && has(detail, "dns_hosting");
  const editRequires: Requirement = { permission: "dns:manage", on: ["domain_folder", detail.folder?.id] };
  const canEdit = editable && canOn(editRequires.permission, "domain_folder", detail.folder?.id);
  const needsAttention = z.managed && (z.conflicts.length > 0 || z.records.some((r) => r.status !== "live"));

  const openEditor = (record: DnsRecord | null) => {
    preview.reset();
    setDraft(draftFor(record));
    setStage("edit");
  };
  const closeAll = () => {
    if (apply.isPending) return;
    setStage(null);
    setDraft(null);
    setReview(null);
    apply.reset();
  };
  const openRemoval = (records: DnsRecord[], kept: DnsRecord | null) => {
    apply.reset();
    setStage(null);
    setRemoval({ records, kept });
  };

  return (
    <div className="flex flex-col gap-6">
      {!z.managed && (
        <Notice
          tone="warning"
          title="DNS for this domain is hosted at another provider"
          actions={
            <Link to={`/domains/${d.id}?tab=nameservers`} className={buttonClass("secondary", "lg")}>
              Go to Nameservers
            </Link>
          }
        >
          {d.display_name} uses custom nameservers
          {d.nameservers.length ? ` (${d.nameservers.join(", ")})` : ""}, so the records that are live sit
          with that DNS provider — add or change them there. Switch back to CortexGrip nameservers to manage
          records here.
        </Notice>
      )}

      {z.managed && (
        <Section title="What's connected" subtitle="Plain-language view of where this domain points">
          {z.connected.map((row, i) => {
            const pill = CONNECTED_PILL[row.status];
            return (
              <ConnectRow
                key={row.key}
                first={i === 0}
                icon={<DomainIcon name={row.key === "email" ? "mail" : "globe"} size={22} />}
                title={row.title}
                detail={row.detail}
                status={
                  <Badge tone={pill.tone} size="sm">
                    {pill.label}
                  </Badge>
                }
              />
            );
          })}
        </Section>
      )}

      {z.zone_failure && (
        <Notice
          tone="danger"
          title="DNS isn't set up yet"
          actions={
            editable && (
              <Button
                size="lg"
                variant="danger"
                requires={editRequires}
                loading={recheck.isPending}
                onClick={() => recheck.mutate()}
              >
                Retry setting up DNS
              </Button>
            )
          }
        >
          {d.display_name} is registered and yours, but CortexGrip couldn't create its DNS zone yet (
          {z.zone_failure.replace(/\.$/, "")}). We keep trying automatically, and you can retry now. Your
          website and email won't resolve until the zone exists.
        </Notice>
      )}

      {needsAttention && (
        <AttentionSection
          zone={z}
          fqdn={d.fqdn}
          editRequires={editable ? editRequires : null}
          onRecheck={() => recheck.mutate()}
          rechecking={recheck.isPending}
          recheckError={recheck.error}
          onRemove={openRemoval}
        />
      )}

      <TemplatesSection
        domain={d}
        managed={z.managed}
        editable={editable}
        editRequires={editRequires}
        onApplied={applied}
      />
      <ZoneFileSection
        domain={d}
        managed={z.managed}
        editable={editable}
        editRequires={editRequires}
        onApplied={applied}
      />

      <Section
        title="DNS records (Advanced)"
        subtitle={
          z.managed
            ? "Raw records — most people never need to open this"
            : "Kept at CortexGrip for when you switch back — not in use while custom nameservers are set"
        }
        action={
          editable && (
            <Button size="lg" variant="secondary" requires={editRequires} onClick={() => openEditor(null)}>
              Add record
            </Button>
          )
        }
      >
        {z.records.length === 0 ? (
          <EmptyState
            title="No records yet"
            description="Add a record to point this domain's website, email or other services somewhere."
            action={
              canEdit && (
                <Button variant="secondary" onClick={() => openEditor(null)}>
                  Add your first record
                </Button>
              )
            }
          />
        ) : (
          <div className="-mx-[22px] overflow-x-auto px-[22px]">
            <Table className="w-full min-w-[600px] text-left text-[13px]">
              <TableHead className="text-xs text-fg-subtle">
                <TableRow>
                  <TableHeaderCell className="w-[90px] pb-2.5 font-normal">Type</TableHeaderCell>
                  <TableHeaderCell className="w-[140px] pb-2.5 font-normal">Host</TableHeaderCell>
                  <TableHeaderCell className="pb-2.5 font-normal">Value</TableHeaderCell>
                  <TableHeaderCell className="w-[90px] pb-2.5 font-normal">TTL</TableHeaderCell>
                  <TableHeaderCell className="w-[60px] pb-2.5 font-normal">
                    <span className="sr-only">Edit</span>
                  </TableHeaderCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {z.records.map((r) => (
                  <TableRow key={r.id} className="border-t border-border">
                    <TableCell className="py-3 pr-3 font-semibold text-fg">{r.type}</TableCell>
                    <TableCell className="py-3 pr-3 break-all text-[#5c5551]">{r.name}</TableCell>
                    <TableCell className="py-3 pr-3 text-[#5c5551]">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="min-w-0 break-all">{recordValue(r)}</span>
                        {r.managed_by === "website" && (
                          <span className="rounded-md bg-[#f3ebe7] px-1.5 py-0.5 text-[10.5px] font-semibold text-[#5c5551]">
                            Website
                          </span>
                        )}
                        {r.status === "pending" && (
                          <Badge tone="warning" size="sm">
                            Pending
                          </Badge>
                        )}
                      </div>
                      {r.status === "error" && (
                        <p className="mt-1 text-xs text-[#b52522]">
                          {r.error ?? "The DNS provider didn't accept this record."}
                        </p>
                      )}
                    </TableCell>
                    <TableCell className="py-3 pr-3 text-[#5c5551]">{r.ttl}</TableCell>
                    <TableCell className="py-3 text-right">
                      {canEdit && (
                        <Button
                          unstyled
                          type="button"
                          className={cx(buttonClass("secondary", "lg"), "px-3 py-2 text-[13px]")}
                          aria-label={`Edit ${r.type} record ${r.name}`}
                          onClick={() => openEditor(r)}
                        >
                          Edit
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </Section>

      {draft && (
        <RecordModal
          open={stage === "edit"}
          draft={draft}
          onChange={setDraft}
          onClose={closeAll}
          onSubmit={() => preview.mutate(changeFor(draft))}
          onDelete={() => draft.record && openRemoval([draft.record], null)}
          submitting={preview.isPending}
          error={preview.error}
          siteName={d.site?.name ?? null}
        />
      )}
      {review && stage === "review" && (
        <ReviewModal
          key={JSON.stringify(review.change)}
          open
          fqdn={d.fqdn}
          change={review.change}
          preview={review.preview}
          onBack={() => {
            apply.reset();
            setStage("edit");
          }}
          onClose={() => reviewing.guard(closeAll)}
          onConfirm={() => apply.mutate([review.change])}
          applying={apply.isPending}
          error={apply.error}
        />
      )}
      {reviewing.modal}
      {removal && (
        <RemoveModal
          open
          fqdn={d.fqdn}
          records={removal.records}
          kept={removal.kept}
          onClose={() => {
            if (apply.isPending) return;
            setRemoval(null);
            setDraft(null);
            apply.reset();
          }}
          onConfirm={() => apply.mutate(removal.records.map((r) => ({ op: "delete", record_id: r.id })))}
          removing={apply.isPending}
          error={apply.error}
        />
      )}
    </div>
  );
}
